import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { HIT_BOARD_STATUS } from 'redux/actions/board';
import { DO_LOADING } from 'redux/actions/loading';
import { activityTypeService, apiActivityType } from '../../../service/ActivityType.service';

const useBridgeRedux = () => {
  const dispatch = useDispatch();

  const setLoading = (value: boolean) => dispatch({
    type: DO_LOADING, payload: value,
  });

  const status = (statusValue: 'success' | 'failed', message: string) => dispatch({
    type: HIT_BOARD_STATUS,
    payload: { status: statusValue, message },
  });

  return {
    setLoading,
    status,
  };
};

export const useActivityTypeList = (companyId: null | string, enabled: boolean = true) => useQuery({
  queryKey: [apiActivityType.activityTypeList, companyId],
  queryFn: () => activityTypeService.getSkillGroup(),
  enabled: !!companyId && enabled,
  retry: false,
  refetchOnWindowFocus: false,
  staleTime: 5 * 60 * 1000,
});

const useMutationCategoryActivityType = <
TPayload, TResponse extends { message: string },
>(
    mutationFn: (payload: TPayload) => Promise<TResponse>,
  ) => {
  const bridge = useBridgeRedux();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onMutate: () => bridge.setLoading(true),
    onSuccess: (responseMessage: TResponse) => {
      const successMessage = responseMessage.message;

      void queryClient.invalidateQueries({
        queryKey: [apiActivityType.activityTypeList],
      });

      bridge.status('success', successMessage);
    },
    onError: (error) => {
      const errorMessage = error.message;
      bridge.status('failed', errorMessage);
    },
    onSettled: () => bridge.setLoading(false),
  });
};

export const useCreateActivityType = () => useMutationCategoryActivityType(activityTypeService.createActivityType);
export const useUpdateActivityType = () => useMutationCategoryActivityType(activityTypeService.updateActivityType);
export const useMoveActivityType = () => useMutationCategoryActivityType(activityTypeService.moveActivityType);
